import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const FooterContainer = styled.footer`
  background: ${({ theme }) => theme.colors.panelBackground};
  padding: 4rem 0 2rem;
  margin-top: auto;
  color: ${({ theme }) => theme.colors.text};
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 0.6fr 0.4fr;
  gap: 3rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const NewsletterSection = styled.div`
  background: ${({ theme }) => theme.colors.contactCardBackground};
  border-radius: 24px;
  padding: 2.5rem 2rem;
  box-shadow: ${({ theme }) => theme.colors.panelShadow};
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.contactCardBorder};
`;

const NewsletterTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1.25rem;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const NewsletterDescription = styled.p`
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
`;

const NewsletterInput = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 0.9rem 1.2rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 999px;
  font-size: 1rem;
  background: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.inputText};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const NewsletterButton = styled.button`
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.9rem 1.8rem;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.05em;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 12px 20px rgba(0, 199, 168, 0.35);
  }
`;

const QuickLinks = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.5rem;
  color: ${({ theme }) => theme.colors.text};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-items: center;
    text-align: center;
  }
`;

const QuickLinksTitle = styled.h3`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.12em;
`;

const LinksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FooterAnchor = styled.button`
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: color 0.3s ease;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const FooterRouteLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.25rem;
  margin-top: -0.5rem;
`;

const SocialLink = styled.a<{ platform?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${({ theme, platform }) => {
    if (platform === 'youtube') return '#FF0000';
    if (platform === 'facebook') return '#1877F2';
    if (platform === 'instagram') return '#E4405F';
    return theme.colors.cardBackground;
  }};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 8px;
  color: ${({ platform, theme }) => {
    if (platform === 'youtube' || platform === 'facebook' || platform === 'instagram') return '#FFFFFF';
    return theme.colors.text;
  }};
  font-size: ${({ platform }) => platform === 'facebook' ? '1.4rem' : '1.2rem'};
  font-weight: ${({ platform }) => platform === 'facebook' ? '900' : '600'};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ platform }) => {
      if (platform === 'youtube') return '0 6px 16px rgba(255, 0, 0, 0.4)';
      if (platform === 'facebook') return '0 6px 16px rgba(24, 119, 242, 0.4)';
      if (platform === 'instagram') return '0 6px 16px rgba(228, 64, 95, 0.4)';
      return '0 6px 16px rgba(0, 199, 168, 0.3)';
    }};
    opacity: 0.9;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: -1;
  }
`;

const LogoImage = styled.img`
  max-width: 420px;
  height: auto;
  filter: ${({ theme }) => theme.mode === 'dark' ? 'brightness(0.9)' : 'none'};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 3rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Footer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // For localhost development, simulate the submission
      if (window.location.hostname === 'localhost') {
        console.log('Newsletter Subscription (localhost simulation):');
        console.log('Email:', email);
        console.log('Subject: New Newsletter Subscription - The Men\'s Well');
        console.log('Message: Newsletter subscription request from', email);
        
        // Simulate delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsSubscribed(true);
        setEmail('');
      } else {
        // Production: Send to PHP script
        const response = await fetch('/newsletter.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        });

        if (response.ok) {
          setIsSubscribed(true);
          setEmail('');
        } else {
          console.error('Newsletter subscription failed');
        }
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const anchorLinks = [
    { label: 'Welcome', target: 'welcome' },
    { label: 'About', target: 'about' },
    { label: 'Support', target: 'offerings' },
    { label: 'Meetings', target: 'meetings' },
    { label: 'Resources', target: 'resources' },
    { label: 'Videos', target: 'videos' },
    { label: 'Feeds', target: 'feeds' },
    { label: 'Contact', target: 'contact' },
  ];

  const handleAnchorSelect = (target: string) => {
    const scrollToSection = () => {
      const el = document.getElementById(target);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: target } });
    } else {
      scrollToSection();
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <NewsletterSection>
          <NewsletterTitle>Subscribe to Our Newsletter</NewsletterTitle>
          <NewsletterDescription>
            Be first to know about new resources, meeting updates, and wellbeing
            insights from The Men's Well community.
          </NewsletterDescription>
          {isSubscribed ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '2rem',
              background: 'rgba(16, 185, 129, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>✅</div>
              <h3 style={{ color: '#10B981', marginBottom: '0.5rem' }}>Thanks for subscribing!</h3>
              <p style={{ color: '#6B7280', margin: 0 }}>
                You'll receive updates about new resources and meeting information.
              </p>
            </div>
          ) : (
            <NewsletterForm onSubmit={handleNewsletterSubmit}>
              <NewsletterInput
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
              />
              <NewsletterButton type="submit" disabled={isSubmitting || !email}>
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </NewsletterButton>
            </NewsletterForm>
          )}
        </NewsletterSection>

        <QuickLinks>
          <QuickLinksTitle>Quick Links</QuickLinksTitle>
          <LinksList>
            {anchorLinks.map((link) => (
              <FooterAnchor key={link.target} onClick={() => handleAnchorSelect(link.target)}>
                {link.label}
              </FooterAnchor>
            ))}
            <FooterRouteLink to="/settings">Settings</FooterRouteLink>
          </LinksList>
          
          <SocialLinks style={{ justifyContent: 'flex-start' }}>
            <SocialLink
              href="https://www.facebook.com/profile.php?id=61556669024858"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              platform="facebook"
            >
              f
            </SocialLink>
            <SocialLink
              href="https://instagram.com/themenswell"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              platform="instagram"
            >
              📷
            </SocialLink>
            <SocialLink
              href="https://www.youtube.com/@TheMensWell"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              platform="youtube"
            >
              ▶
            </SocialLink>
          </SocialLinks>
        </QuickLinks>

        <LogoSection>
          <LogoImage 
            src="/The-Mens-Well_D24-special-mockup.jpg" 
            alt="The Men's Well Logo" 
          />
        </LogoSection>
      </FooterContent>


      <Copyright>
        <p>© 2025 by The Men's Well. All rights reserved.</p>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
